import { ConfigService } from '@services/config.service';
import { UnitNode, UnitService } from '@services/unit.service';
import { Singleton } from '@visiba/instantiation';
import { EventEmitter, safeJsonParse } from '@visiba/std';
import { Logger } from '../../modules/logger/logger';
import { RegisterRecentVisitService } from './register_recent_visit.service';

export class CouldNotLoadUnitError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'CouldNotLoadUnitError';
		Object.setPrototypeOf(this, CouldNotLoadUnitError.prototype);
	}
}

export class NoLocationCouldHaveBeenSet extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'NoLocationCouldHaveBeenSet';
		Object.setPrototypeOf(this, NoLocationCouldHaveBeenSet.prototype);
	}
}

export class UnitIsNotEntrance extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'UnitIsNotEntrance';
		Object.setPrototypeOf(this, UnitIsNotEntrance.prototype);
	}
}

@Singleton()
export class LocationSelectorService {
	public readonly events = new EventEmitter<{
		onCommitting: number;
		onCommitted: number;
	}>();

	private selectingLocation: UnitNode | null = null;
	private selectedLocation: UnitNode | null = null;

	private selectingEntrance: UnitNode | null = null;
	private selectedEntrance: UnitNode | null = null;

	/** Gets selected unit location, throws exception ({@link NoLocationCouldHaveBeenSet}) if is not set or is about to be  */
	public get location(): UnitNode {
		return this.getHeadingTowardsOrCurrentLocation();
	}

	/** Gets selected location UnitId, throws exception ({@link NoLocationCouldHaveBeenSet}) if is not set or is about to be  */
	public get locationUnitId(): number {
		const unit = this.getHeadingTowardsOrCurrentLocation();

		return unit.id;
	}

	/** Gets selected entrance UnitId  */
	public get entrance(): UnitNode | null {
		return this.getHeadingTowardsOrCurrentEntrance();
	}

	/** Gets UnitId of the selected entrance unit  */
	public get entranceUnitId(): number | null {
		const unit = this.getHeadingTowardsOrCurrentEntrance();
		if (unit == null) return null;

		return unit.id;
	}

	constructor(
		private readonly unitService: UnitService,
		private readonly configService: ConfigService,
		private readonly recentVisitService: RegisterRecentVisitService,
	) {
		// Empty
	}

	public async commitByUnitId(unitId: number): Promise<void> {
		try {
			await this.load(unitId);
		} catch (error: unknown) {
			let reason: string;
			if (error instanceof Error) {
				reason = error.message;
			} else {
				reason = safeJsonParse(error) ?? 'unknown';
			}

			throw new CouldNotLoadUnitError(`There was an problem loading unit id "${unitId}", reason:\n${reason}`);
		}
	}

	public async commitByRoute(route: string): Promise<void> {
		try {
			const unitId = this.unitService.backTraceUnitIdFromRoute(route);
			await this.load(unitId);
		} catch (error: unknown) {
			let reason: string;
			if (error instanceof Error) {
				reason = error.message;
			} else {
				reason = safeJsonParse(error) ?? 'unknown';
			}

			throw new CouldNotLoadUnitError(`There was an problem loading unit route "${route}", reason:\n${reason}`);
		}
	}

	private async load(unitId: number): Promise<void> {
		if (unitId === this.selectedLocation?.id || unitId === this.selectingLocation?.id) return;

		const unit = this.unitService.getUnitNodeInTree(unitId);
		const shouldSelectEntrance = unit.isEntrance && this.selectedEntrance?.id !== unitId;

		this.selectingLocation = unit;
		this.selectedLocation = null;

		if (shouldSelectEntrance) {
			this.selectingEntrance = unit;
			this.selectedEntrance = null;
		}

		this.events.emit('onCommitting', unit.id);
		Logger.assert(Logger.Level.verbose, `Selecting UnitId: ${unitId}`);

		await this.configService.loadConfig(unit.id);

		this.selectingLocation = null;
		this.selectedLocation = unit;

		if (shouldSelectEntrance) {
			this.selectingEntrance = null;
			this.selectedEntrance = unit;

			this.recentVisitService.registerVisit(unit.receptionId);
		}

		this.events.emit('onCommitted', unit.id);
		Logger.assert(Logger.Level.verbose, `Selected UnitId: ${unitId}`);
	}

	private getHeadingTowardsOrCurrentLocation(): UnitNode {
		if (this.selectingLocation == null) {
			if (this.selectedLocation != null) {
				return this.selectedLocation;
			}

			throw new NoLocationCouldHaveBeenSet('No location has been set or tried to been set');
		}

		return this.selectingLocation;
	}

	private getHeadingTowardsOrCurrentEntrance(): UnitNode | null {
		return this.selectingEntrance ?? this.selectedEntrance;
	}
}
